import type { PopoverRootProps } from "@v1/components/designSystem/popover/Popover";
import type { DeprecatedToggleOption } from "@v1/components/designSystem/toggle/ToggleGroup";
import type { ShopifySellingPlan } from "replo-runtime/shared/types";
import type {
  DateFormatOptions,
  Formatter,
  ReferenceType,
} from "schemas/dynamicData";

import React, { useCallback, useMemo } from "react";

import Popover from "@v1/components/designSystem/popover/Popover";
import Selectable from "@v1/components/designSystem/Selectable";
import SwitchWithDescription from "@v1/components/designSystem/switch/SwitchWithDescription";
import { DeprecatedToggleGroup } from "@v1/components/designSystem/toggle/ToggleGroup";
import {
  selectDraftComponentText,
  selectDraftComponentTextDynamicDataInfo,
  selectEvaluatedDraftComponentText,
} from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import {
  shouldAllowBuyboxOptionsByDynamicDataPath,
  updateFormatters,
} from "@v1/utils/dynamic-data";
import { stripHtmlTags } from "@v1/utils/string";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { formatterSchema } from "schemas/dynamicData";
import { z } from "zod";

import ControlGroup from "./element-editor/components/extras/ControlGroup";

const LOCAL_STORAGE_CURRENCY_FORMAT_KEY = "replo-default-currency-format";

export const useDefaultCurrencyFormat = () => {
  const [defaultCurrencyFormatters, setDefaultCurrencyFormatters] =
    useLocalStorageState(
      LOCAL_STORAGE_CURRENCY_FORMAT_KEY,
      [{ type: "currency" }] as Formatter[],
      {
        schema: z.array(formatterSchema),
      },
    );
  return {
    defaultCurrencyFormatters,
    setDefaultCurrencyFormatters,
  };
};

type DynamicDataFormatterProps = Pick<
  Omit<PopoverRootProps, "shouldClose">,
  "isDefaultOpen" | "shouldIgnoreOutsideInteractions"
> & {
  trigger: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  sideOffset?: number;
  location?: "canvas" | "designPanel";
  onChange: (value: string) => void;
  initialValue?: Formatter[];
  referenceType: ReferenceType;
  triggerClassName?: string;
  sellingPlans: ShopifySellingPlan[];
};

const FormattedPreview = () => {
  const _evaluatedValue = useEditorSelector(selectEvaluatedDraftComponentText);
  if (!_evaluatedValue) {
    return null;
  }

  const evaluatedValue = stripHtmlTags(_evaluatedValue);
  return (
    <p className="bg-surface header-h3 flex items-center justify-center rounded-sm py-2">
      {evaluatedValue}
    </p>
  );
};

type FormatterToggleProps = {
  label: string;
  selectedValue: string;
  options: DeprecatedToggleOption[];
  onChange: (value: string) => void;
};

const FormatterToggleControl = ({
  label,
  selectedValue,
  options,
  onChange,
}: FormatterToggleProps) => (
  <ControlGroup label={label}>
    <DeprecatedToggleGroup
      size="sm"
      selectedValue={selectedValue}
      options={options}
      onChange={onChange}
    />
  </ControlGroup>
);

function useFormatter<T extends Formatter>(
  initialFormatters: T[],
  location: "canvas" | "designPanel",
  onChange: (value: string) => void,
) {
  const text = useEditorSelector(selectDraftComponentText);
  const analytics = useLogAnalytics();
  const { setDefaultCurrencyFormatters } = useDefaultCurrencyFormat();

  const updateFormatter = useCallback(
    (newFormatters: Formatter[]) => {
      if (!text) {
        return;
      }
      const type = newFormatters.some(({ type }) => type === "date")
        ? "date"
        : "currency";
      analytics("editor.dynamicData.format", {
        location,
        type,
        formatters: newFormatters,
      });
      const newDynamicDataValue = updateFormatters(newFormatters, text);
      if (newDynamicDataValue) {
        if (type === "currency") {
          setDefaultCurrencyFormatters(newFormatters);
        }
        onChange(newDynamicDataValue);
      }
    },
    [text, onChange, analytics, setDefaultCurrencyFormatters, location],
  );

  const hasFormatterType = useCallback(
    (type: string) => {
      return initialFormatters.some((formatter) => formatter.type === type);
    },
    [initialFormatters],
  );

  return {
    formatters: initialFormatters,
    hasFormatterType,
    updateFormatter,
  };
}

export const DynamicDataFormatter = ({
  trigger,
  isDefaultOpen,
  side = "bottom",
  sideOffset = 4,
  initialValue,
  onChange,
  location = "designPanel",
  referenceType,
  triggerClassName,
  sellingPlans,
}: DynamicDataFormatterProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const header = exhaustiveSwitch({ type: referenceType })({
    currency: "Price Formatting",
    date: "Date Formatting",
  });

  const formatterNode = exhaustiveSwitch({ type: referenceType })({
    currency: () => (
      <CurrencyFormatter
        formatters={initialValue ?? []}
        location={location}
        onChange={onChange}
        sellingPlans={sellingPlans}
      />
    ),
    date: () => (
      <DateFormatter
        formatters={initialValue ?? []}
        location={location}
        onChange={onChange}
      />
    ),
  });

  return (
    <Popover.Root
      isDefaultOpen={isDefaultOpen}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      shouldIgnoreOutsideInteractions={false}
    >
      <Popover.Trigger className={triggerClassName}>{trigger}</Popover.Trigger>
      <Popover.Content
        shouldPreventDefaultOnInteractOutside={false}
        side={side}
        sideOffset={sideOffset}
        align="center"
        title={header}
        className="w-[267px] px-4 py-4"
      >
        {formatterNode}
      </Popover.Content>
    </Popover.Root>
  );
};

function isCurrencyFormatter(
  formatter: Formatter,
): formatter is Extract<Formatter, { type: "currency" }> {
  return formatter.type === "currency";
}

const CurrencyFormatter = ({
  formatters,
  location,
  onChange,
  sellingPlans,
}: {
  formatters: Formatter[];
  location: "canvas" | "designPanel";
  onChange: (value: string) => void;
  sellingPlans: ShopifySellingPlan[];
}) => {
  const dynamicDataInfoList = useEditorSelector(
    selectDraftComponentTextDynamicDataInfo,
  );
  const dynamicDataInfo = dynamicDataInfoList?.[0];
  const shouldAllowBuyboxOptions = shouldAllowBuyboxOptionsByDynamicDataPath(
    dynamicDataInfo?.path,
  );

  const { hasFormatterType, updateFormatter } = useFormatter(
    formatters,
    location,
    onChange,
  );

  const handleToggleFormatter = useCallback(
    (
      opts:
        | {
            isEnabled: false;
            formatterType:
              | "discount"
              | "quantityMultiplier"
              | "currency"
              | "rounded";
          }
        | {
            isEnabled: true;
            formatterType: "discount";
            sellingPlanId: number | "_selected_";
          }
        | {
            isEnabled: boolean;
            formatterType: "quantityMultiplier" | "currency" | "rounded";
          },
    ) => {
      let newFormatters = [...formatters];
      if (opts.isEnabled) {
        if (opts.formatterType === "discount") {
          newFormatters = newFormatters.filter(
            (formatter) => formatter.type !== "discount",
          );
          const newFormatter: Extract<Formatter, { type: "discount" }> =
            opts.sellingPlanId === "_selected_"
              ? {
                  type: "discount",
                  discountType: "selectedSellingPlan",
                }
              : {
                  type: "discount",
                  discountType: "specificSellingPlan",
                  sellingPlanId: opts.sellingPlanId,
                };
          newFormatters.push(newFormatter);
        } else {
          newFormatters.push({
            type: opts.formatterType,
          });
        }
      } else {
        newFormatters = newFormatters.filter(
          (formatter) => formatter.type !== opts.formatterType,
        );
      }
      updateFormatter(newFormatters);
    },
    [formatters, updateFormatter],
  );

  const toggleCurrencyCode = useCallback(
    (isEnabled: boolean) => {
      const newFormatters = formatters.map((formatter) => {
        if (isCurrencyFormatter(formatter)) {
          return {
            ...formatter,
            showCurrencyCode: isEnabled,
          };
        }
        return formatter;
      });
      updateFormatter(newFormatters);
    },
    [formatters, updateFormatter],
  );

  const currencyFormatter = formatters.find((formatter) => {
    return isCurrencyFormatter(formatter);
  });

  const currencyIsEnabled = Boolean(currencyFormatter);

  const sellingPlanSelectableOptions = useMemo(() => {
    return [
      { value: "_selected_", label: "Selected Selling Plan" },
      ...sellingPlans.map((sellingPlan) => ({
        label: sellingPlan.name,
        value: String(sellingPlan.id),
      })),
    ];
  }, [sellingPlans]);
  const discountFormatter = formatters.find(
    (formatter) => formatter.type === "discount",
  );
  let sellingPlanValue: string | undefined;
  if (discountFormatter) {
    if (discountFormatter.discountType === "selectedSellingPlan") {
      sellingPlanValue = "_selected_";
    } else {
      sellingPlanValue = String(discountFormatter.sellingPlanId);
    }
  }

  return (
    <>
      <FormattedPreview />
      <div className="border-border mb-2 border-b"></div>
      <FormatterToggleControl
        label="Rounding"
        selectedValue={hasFormatterType("rounded") ? "rounded" : "precise"}
        options={[
          { label: "Precise", value: "precise" },
          { label: "Rounded", value: "rounded" },
        ]}
        onChange={(value) => {
          return handleToggleFormatter({
            isEnabled: value === "rounded",
            formatterType: "rounded",
          });
        }}
      />
      <FormatterToggleControl
        label="Currency"
        selectedValue={currencyIsEnabled ? "currency" : "plain"}
        options={[
          { label: "Currency", value: "currency" },
          { label: "Plain", value: "plain" },
        ]}
        onChange={(value) => {
          return handleToggleFormatter({
            isEnabled: value === "currency",
            formatterType: "currency",
          });
        }}
      />
      {currencyFormatter && (
        <SwitchWithDescription
          label="Show Currency Code"
          isOn={Boolean(currencyFormatter.showCurrencyCode)}
          size="sm"
          onChange={(isEnabled) => toggleCurrencyCode(isEnabled)}
          variant="inline"
        />
      )}

      {shouldAllowBuyboxOptions && (
        <>
          <div className="flex flex-col gap-1">
            <SwitchWithDescription
              label="Apply Selling Plan Discount"
              description="Display price with a selling plan's discount"
              isOn={hasFormatterType("discount")}
              size="sm"
              onChange={(isEnabled) => {
                if (isEnabled) {
                  handleToggleFormatter({
                    isEnabled,
                    formatterType: "discount",
                    sellingPlanId: "_selected_",
                  });
                } else {
                  handleToggleFormatter({
                    isEnabled: false,
                    formatterType: "discount",
                  });
                }
              }}
              variant="inline"
            />
            {hasFormatterType("discount") &&
              sellingPlanSelectableOptions.length > 1 && (
                <Selectable
                  options={sellingPlanSelectableOptions}
                  areOptionsSearchable={sellingPlanSelectableOptions.length > 5}
                  value={sellingPlanValue}
                  onSelect={(value) => {
                    if (value === "_selected_") {
                      handleToggleFormatter({
                        isEnabled: true,
                        formatterType: "discount",
                        sellingPlanId: "_selected_",
                      });
                    } else {
                      handleToggleFormatter({
                        isEnabled: true,
                        formatterType: "discount",
                        sellingPlanId: Number(value),
                      });
                    }
                  }}
                />
              )}
          </div>
          <SwitchWithDescription
            label="Apply Selected Quantity"
            description="Display price based on the selected quantity"
            isOn={hasFormatterType("quantityMultiplier")}
            size="sm"
            onChange={(isEnabled) => {
              return handleToggleFormatter({
                isEnabled,
                formatterType: "quantityMultiplier",
              });
            }}
            variant="inline"
          />
        </>
      )}
    </>
  );
};

const DEFAULT_TEXT_DATE_OPTIONS: DateFormatOptions = {
  format: "text",
  includeYear: true,
  includeWeekday: false,
};

const DEFAULT_NUMBER_DATE_OPTIONS: DateFormatOptions = {
  format: "number",
  includeYear: true,
  separator: "-",
  order: "month-first",
};

function isDateFormatter(
  formatter: Formatter,
): formatter is { type: "date"; dateOptions: DateFormatOptions } {
  return formatter.type === "date" && Boolean(formatter.dateOptions);
}

const DateFormatter = ({
  formatters,
  location,
  onChange,
}: {
  formatters: Formatter[];
  location: "canvas" | "designPanel";
  onChange: (value: string) => void;
}) => {
  const { updateFormatter } = useFormatter(formatters, location, onChange);

  const dateFormat = useMemo(() => {
    const existing = formatters.find(isDateFormatter);
    if (!existing) {
      return {
        type: "date" as const,
        dateOptions: DEFAULT_TEXT_DATE_OPTIONS,
      };
    }
    return existing;
  }, [formatters]);

  const dateOptions = dateFormat.dateOptions;

  const handleToggleFormatType = useCallback(
    (newType: string) => {
      const newDateFormat: DateFormatOptions = {
        ...(newType === "text"
          ? DEFAULT_TEXT_DATE_OPTIONS
          : DEFAULT_NUMBER_DATE_OPTIONS),
        includeYear: dateOptions.includeYear,
      };

      let newFormatters = formatters.map((formatter) => {
        if (formatter.type !== "date") {
          return formatter;
        }
        return { type: "date", dateOptions: newDateFormat };
      });

      if (newFormatters.length < 1) {
        newFormatters = [{ type: "date", dateOptions: newDateFormat }];
      }

      updateFormatter(newFormatters as Formatter[]);
    },
    [formatters, dateOptions, updateFormatter],
  );

  const handleToggleAttribute = useCallback(
    (
      key: "includeYear" | "includeWeekday" | "order" | "separator",
      value: any,
    ) => {
      const newDateOptions = {
        ...dateOptions,
        [key]: value,
      };

      const newFormatters = formatters.map((formatter) => {
        if (formatter.type !== "date") {
          return formatter;
        }
        return {
          ...formatter,
          dateOptions: newDateOptions,
        };
      });

      updateFormatter(newFormatters);
    },
    [formatters, dateOptions, updateFormatter],
  );

  return (
    <>
      <FormattedPreview />
      <FormatterToggleControl
        label="Format"
        selectedValue={dateOptions.format}
        options={[
          { label: "Number", value: "number" },
          { label: "Text", value: "text" },
        ]}
        onChange={handleToggleFormatType}
      />

      {dateOptions.format === "text" ? (
        <>
          <FormatterToggleControl
            label="Year"
            selectedValue={
              dateOptions.includeYear ? "include-year" : "exclude-year"
            }
            options={[
              { label: "Included", value: "include-year" },
              { label: "Hidden", value: "exclude-year" },
            ]}
            onChange={(newValue) => {
              return handleToggleAttribute(
                "includeYear",
                newValue === "include-year",
              );
            }}
          />

          <FormatterToggleControl
            label="Weekday"
            selectedValue={
              dateOptions.includeWeekday ? "include-weekday" : "exclude-weekday"
            }
            options={[
              { label: "Included", value: "include-weekday" },
              { label: "Hidden", value: "exclude-weekday" },
            ]}
            onChange={(newValue) => {
              return handleToggleAttribute(
                "includeWeekday",
                newValue === "include-weekday",
              );
            }}
          />
        </>
      ) : (
        <>
          <FormatterToggleControl
            label="Order"
            selectedValue={dateOptions.order}
            options={[
              {
                label: "Month First",
                value: "month-first",
              },
              {
                label: "Day First",
                value: "day-first",
              },
            ]}
            onChange={(newValue) => handleToggleAttribute("order", newValue)}
          />

          <FormatterToggleControl
            label="Separator"
            selectedValue={dateOptions.separator}
            options={[
              { label: "-", value: "-" },
              { label: "/", value: "/" },
            ]}
            onChange={(newValue) => {
              return handleToggleAttribute("separator", newValue);
            }}
          />

          <FormatterToggleControl
            label="Year"
            selectedValue={
              dateOptions.includeYear ? "include-year" : "exclude-year"
            }
            options={[
              { label: "Included", value: "include-year" },
              { label: "Hidden", value: "exclude-year" },
            ]}
            onChange={(newValue) => {
              return handleToggleAttribute(
                "includeYear",
                newValue === "include-year",
              );
            }}
          />
        </>
      )}
    </>
  );
};
